/**
 * Configuração aritmética do domínio.
 *
 * Regra da spec funcional §2.3: `NUMERIC` em toda parte, ponto flutuante PROIBIDO
 * em campo de volume, dinheiro ou preço. Aqui isso vira decimal.js configurado
 * uma única vez, com arredondamento explícito.
 *
 * A configuração é global do módulo Decimal, então mora só neste arquivo e
 * ninguém mais chama `Decimal.set`.
 */
// Import NOMEADO, não default: sob NodeNext o default de decimal.js resolve
// para o namespace (sem construtor nem estáticos). O nomeado traz a entidade
// fundida classe+namespace, que é construtível e tem ROUND_HALF_UP.
import { Decimal } from "decimal.js";
Decimal.set({
    // Folga generosa para intermediários; o arredondamento acontece na borda,
    // ao materializar Volume/Dinheiro/Preco, nunca no meio da conta.
    precision: 40,
    // Convenção fiscal brasileira.
    rounding: Decimal.ROUND_HALF_UP,
    // Impede notação exponencial em toString(). Sem isso, um valor muito pequeno
    // vira "1e-7" e o Postgres recebe lixo no campo NUMERIC.
    toExpNeg: -30,
    toExpPos: 40,
});
export { Decimal };
/** Casas decimais por tipo de grandeza. Espelham o schema do banco. */
export const ESCALA = {
    /** volume_m3 NUMERIC(14,6) */
    VOLUME: 6,
    /** valores monetários NUMERIC(15,2) */
    DINHEIRO: 2,
    /** preço unitário — mais casas, para reconciliar total × quantidade (§4.3) */
    PRECO: 6,
};
/** Casas usadas só para EXIBIR. Nunca entram em conta (design §6.3). */
export const EXIBICAO = {
    VOLUME: 3,
    DINHEIRO: 2,
    PERCENTUAL: 1,
};
/**
 * π com 40 dígitos, fixo.
 *
 * Deliberadamente uma constante e não `Math.PI` nem um π calculado em runtime:
 * a cubagem precisa dar bit-a-bit o mesmo resultado no servidor e no aparelho
 * Android (spec funcional §2.4). Constante literal é a única garantia disso.
 */
export const PI = new Decimal("3.1415926535897932384626433832795028841972");
export class ErroDominio extends Error {
    constructor(mensagem) {
        super(mensagem);
        this.name = "ErroDominio";
    }
}
/**
 * Converte entrada crua em Decimal, recusando o que não é número finito.
 * `number` é aceito por conveniência de borda (JSON), mas números com mais de
 * 15 dígitos significativos já chegam corrompidos — por isso a preferência é
 * sempre `string`.
 */
export function paraDecimal(v, campo) {
    if (v instanceof Decimal) {
        if (!v.isFinite())
            throw new ErroDominio(`${campo}: valor não finito`);
        return v;
    }
    if (typeof v === "number") {
        if (!Number.isFinite(v))
            throw new ErroDominio(`${campo}: valor não finito`);
        const d = new Decimal(v);
        if (!d.isFinite())
            throw new ErroDominio(`${campo}: valor não finito`);
        return d;
    }
    const texto = v.trim();
    if (texto === "")
        throw new ErroDominio(`${campo}: vazio`);
    let d;
    try {
        d = new Decimal(texto);
    }
    catch {
        throw new ErroDominio(`${campo}: "${v}" não é um número`);
    }
    if (!d.isFinite())
        throw new ErroDominio(`${campo}: valor não finito`);
    return d;
}
/** Arredonda para `casas` com ROUND_HALF_UP e devolve texto em notação fixa. */
export function fixar(d, casas) {
    return d.toDecimalPlaces(casas, Decimal.ROUND_HALF_UP).toFixed(casas);
}
/** Formata em pt-BR. Só para exibição — o resultado nunca volta para uma conta. */
export function formatarPtBr(d, casas) {
    const fixo = d.toDecimalPlaces(casas, Decimal.ROUND_HALF_UP).toFixed(casas);
    const negativo = fixo.startsWith("-");
    const semSinal = negativo ? fixo.slice(1) : fixo;
    const partes = semSinal.split(".");
    const inteiro = partes[0] ?? "0";
    const fracao = partes[1];
    const comMilhar = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const corpo = fracao === undefined ? comMilhar : `${comMilhar},${fracao}`;
    return negativo ? `-${corpo}` : corpo;
}
//# sourceMappingURL=numero.js.map